import { resolveChainConfig } from "../config/chains";

// Methods that are pure reads or gas estimation - safe to serve from a public RPC
// Signing / account / wallet_* methods are absent here so they hit the wallet.
// eth_chainId / net_version MUST stay on the wallet: routing them to a public
// RPC frozen at wrap-time made getNetwork() keep reporting mainnet after a
// switch to Sepolia, so account checks hit the wrong chain.
const ALCHEMY_ROUTED = new Set([
  "eth_blockNumber",
  "eth_call",
  "eth_estimateGas",
  "eth_gasPrice",
  "eth_maxPriorityFeePerGas",
  "eth_feeHistory",
  "eth_getBalance",
  "eth_getCode",
  "eth_getStorageAt",
  "eth_getTransactionCount",
  "eth_getBlockByNumber",
  "eth_getBlockByHash",
  "eth_getTransactionByHash",
  "eth_getTransactionReceipt",
  "eth_getBlockReceipts",
  "eth_getLogs",
  "eth_getProof",
]);

// Chains where the wallet keeps pricing transactions itself. Tempo pays fees
// in stablecoin fee tokens, so EIP-1559 numbers from here don't apply.
const WALLET_PRICED_CHAIN_KEYS = new Set(["tempo"]);

// Headroom over the current base fee for the fees suggested below.
const BASE_FEE_MULTIPLIER = 3;

const toQuantity = (value) => "0x" + value.toString(16);

function normalizeChainId(chainId) {
  if (chainId == null) return null;
  if (typeof chainId === "string") {
    return chainId.startsWith("0x") ? parseInt(chainId, 16) : Number(chainId);
  }
  return Number(chainId);
}

// Resolve the configured public RPC URL for a chain id.
// Uses the shared resolver so mainnet/testnet siblings stay distinct.
export function rpcUrlForChain(chainId) {
  const idNum = normalizeChainId(chainId);
  if (!idNum) return null;
  const cfg = resolveChainConfig(idNum);
  return cfg?.rpcUrl || null;
}

/**
 * Wrap a wallet EIP-1193 provider so reads/estimateGas go to a reliable public RPC
 * while signing stays with the wallet.
 *
 * @param {object} walletProvider - Raw EIP-1193 provider (Privy's).
 * @param {string|number} chainIdHint - Active chain id (hex or decimal).
 * @returns {object} A drop-in EIP-1193 provider (Proxy) usable by ethers v5/v6.
 */
export function retargetHybridChain(walletProvider, chainIdHint) {
  if (!walletProvider || !walletProvider.__isHybridProvider) return walletProvider;
  walletProvider.__setHybridChain?.(chainIdHint);
  return walletProvider;
}

export function makeHybridEip1193(walletProvider, chainIdHint) {
  if (!walletProvider || typeof walletProvider.request !== "function") {
    return walletProvider;
  }
  if (walletProvider.__isHybridProvider) {
    retargetHybridChain(walletProvider, chainIdHint);
    return walletProvider;
  }

  let rpcUrl = rpcUrlForChain(chainIdHint);
  let chainKey = resolveChainConfig(normalizeChainId(chainIdHint))?.id || null;
  let rid = 1;

  const retarget = (cid) => {
    const next = rpcUrlForChain(cid);
    if (next) {
      rpcUrl = next;
      chainKey = resolveChainConfig(normalizeChainId(cid))?.id || null;
    }
  };

  // Keep the routed RPC in sync with the wallet's active chain so cross-chain reads
  // never hit the wrong network..subscribe once per underlying provider (guarded).
  try {
    if (!walletProvider.__hybridChainSub && typeof walletProvider.on === "function") {
      walletProvider.__hybridChainSub = true;
      walletProvider.on("chainChanged", retarget);
    }
  } catch {
    /* provider may be frozen or lack events - safe to ignore */
  }

  async function httpRpc(method, params, { retries = 3 } = {}) {
    let lastErr;
    for (let attempt = 0; attempt <= retries; attempt++) {
      let res;
      try {
        res = await fetch(rpcUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            jsonrpc: "2.0",
            id: rid++,
            method,
            params: params || [],
          }),
        });
      } catch (e) {
        const err = new Error("Read RPC network error");
        err.isNetworkError = true;
        lastErr = err;
        if (attempt < retries) {
          await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
          continue;
        }
        throw err;
      }
      // Official public RPCs (esp. Base) often return 429 under load. Back off
      // briefly before falling through to the wallet provider.
      if (res.status === 429 && attempt < retries) {
        await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
        continue;
      }
      if (!res.ok) {
        const err = new Error(`Read RPC HTTP ${res.status}`);
        err.isNetworkError = true;
        err.status = res.status;
        throw err;
      }
      let json;
      try {
        json = await res.json();
      } catch (e) {
        const err = new Error("Read RPC bad JSON");
        err.isNetworkError = true;
        throw err;
      }
      if (json.error) {
        const err = new Error(json.error.message || "RPC error");
        err.code = json.error.code;
        err.data = json.error.data;
        err.isRpcError = true;
        throw err;
      }
      return json.result;
    }
    throw lastErr || new Error("Read RPC failed");
  }

  // Wallets price any transaction the app didn't, and MetaMask's estimate can
  // trail a rising base fee: on Arbitrum Sepolia it sent maxFeePerGas just
  // under the block's base fee and the network rejected the transaction.
  // Neither the SDK nor the ethers signers set fees on eth_sendTransaction, so
  // they're suggested here, where every wallet transaction passes. Wallets use
  // site-suggested fees, and maxFeePerGas is only a ceiling: the chain still
  // charges the actual base fee. Anything unusual is left to the wallet.
  async function withSuggestedFees(params) {
    const tx = Array.isArray(params) ? params[0] : null;
    if (!tx || typeof tx !== "object") return params;
    // Already priced (executeContractTxWithGasFallback), or explicitly legacy.
    if (tx.maxFeePerGas != null || tx.gasPrice != null) return params;
    if (tx.type === "0x0" || tx.type === 0) return params;
    if (chainKey && WALLET_PRICED_CHAIN_KEYS.has(chainKey)) return params;
    try {
      const [block, tip] = await Promise.all([
        httpRpc("eth_getBlockByNumber", ["latest", false]),
        httpRpc("eth_maxPriorityFeePerGas", []),
      ]);
      // No base fee (pre-1559 chain) or no tip data: the wallet knows better.
      if (!block?.baseFeePerGas || tip == null) return params;
      const priorityFee = BigInt(tip);
      const maxFee =
        BigInt(block.baseFeePerGas) * BigInt(BASE_FEE_MULTIPLIER) + priorityFee;
      return [
        { ...tx, maxFeePerGas: toQuantity(maxFee), maxPriorityFeePerGas: toQuantity(priorityFee) },
        ...params.slice(1),
      ];
    } catch {
      // Never block a transaction on the fee lookup.
      return params;
    }
  }

  const handler = {
    get(target, prop, receiver) {
      if (prop === "__isHybridProvider") return true;
      if (prop === "__setHybridChain") {
        return retarget;
      }
      if (prop === "request") {
        return async (args) => {
          const method = args && args.method;
          const params = args && args.params;
          if (method === "eth_sendTransaction" && rpcUrl) {
            return target.request({ ...args, params: await withSuggestedFees(params) });
          }
          if (rpcUrl && ALCHEMY_ROUTED.has(method)) {
            try {
              return await httpRpc(method, params);
            } catch (err) {
              if (err && err.isRpcError) throw err;
              return target.request(args);
            }
          }
          return target.request(args);
        };
      }
      const value = Reflect.get(target, prop, receiver);
      return typeof value === "function" ? value.bind(target) : value;
    },
  };

  return new Proxy(walletProvider, handler);
}
